import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@finance/components/Layout";
import { api, errMsg, openPdf } from "@finance/lib/api";
import { FileText, ArrowSquareOut, CircleNotch } from "@phosphor-icons/react";

function Preview({ docKey }) {
  const [pages, setPages] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setPages(null); setErr("");
    api.get(`/doc-gallery/${docKey}/preview`).then((r) => setPages(r.data.pages)).catch((e) => setErr(errMsg(e)));
  }, [docKey]);
  if (err) return <div className="p-6 text-sm text-red-700" data-testid="gallery-preview-error">{err}</div>;
  if (!pages) return <div className="flex flex-1 items-center justify-center p-10 text-sm text-gray-500" data-testid="gallery-loading"><CircleNotch size={18} className="mr-2 animate-spin" />Rendering sample…</div>;
  return (
    <div className="flex-1 space-y-4 overflow-y-auto bg-gray-100 p-3 sm:p-6 dark:bg-[#0b1220]" data-testid="gallery-pages">
      {pages.map((src, i) => <img key={i} src={src} alt={`Page ${i + 1}`} className="mx-auto w-full max-w-[900px] rounded-sm bg-white shadow-md" data-testid={`gallery-page-${i + 1}`} />)}
    </div>
  );
}

function DocList({ docs, active, onPick }) {
  const groups = [...new Set(docs.map((d) => d.group))];
  return (
    <div className="space-y-4" data-testid="gallery-list">
      {groups.map((g) => (
        <div key={g}>
          <div className="lx-label mb-1.5">{g}</div>
          <div className="space-y-1">
            {docs.filter((d) => d.group === g).map((d) => (
              <button key={d.key} onClick={() => onPick(d.key)} data-testid={`gallery-item-${d.key}`}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${active === d.key ? "bg-[#1B4383] text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                <FileText size={16} />{d.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DocumentGallery() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/doc-gallery").then((r) => { setDocs(r.data); setActive(r.data[0]?.key); }).catch((e) => setError(errMsg(e)));
  }, []);
  const cur = docs.find((d) => d.key === active);
  return (
    <Layout>
      <PageHeader title="Document Gallery" subtitle="Preview every document Finance Hub issues, filled with sample data and watermarked SAMPLE. Nothing here is saved or posted." />
      {error ? <div className="m-8 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="gallery-error">{error}</div> : (
        <div className="grid gap-5 p-4 sm:p-8 lg:grid-cols-[260px_1fr]">
          <div className="hidden rounded-sm border border-gray-200 bg-white p-4 dark:border-[#243049] dark:bg-[#0f172a] lg:block lg:max-h-[calc(100vh-180px)] lg:overflow-y-auto"><DocList docs={docs} active={active} onPick={setActive} /></div>
          <select className="h-10 w-full rounded-sm border border-gray-200 bg-white px-2 text-sm lg:hidden" value={active || ""} onChange={(e) => setActive(e.target.value)} data-testid="gallery-select">
            {docs.map((d) => <option key={d.key} value={d.key}>{d.group} — {d.label}</option>)}
          </select>
          <div className="flex min-h-[70vh] min-w-0 flex-col overflow-hidden rounded-sm border border-gray-200 bg-white lg:max-h-[calc(100vh-180px)] dark:border-[#243049] dark:bg-[#0f172a]" data-testid="gallery-preview">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
              <div className="text-sm font-semibold text-slate-800" data-testid="gallery-preview-title">{cur?.label || "—"}</div>
              {cur && <button onClick={() => openPdf(`doc-gallery/${cur.key}/pdf`)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1B4383] hover:underline" data-testid="gallery-open-tab"><ArrowSquareOut size={14} />Open in new tab</button>}
            </div>
            {cur && <Preview docKey={cur.key} />}
          </div>
        </div>
      )}
    </Layout>
  );
}
